import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { helpService } from '../../services/help';
import { useDialogFocus } from './useDialogFocus';
import './Help.css';

/** Collect a short problem report with optional private screenshot evidence. */
export default function ReportProblem({ onClose }: { onClose: () => void }) {
  const { currentUser, isH2okayAdmin } = useAuth();
  const { showToast } = useToast();
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [version, setVersion] = useState('Current backend version');
  const ref = useRef<HTMLDivElement>(null);
  useDialogFocus(ref);
  useEffect(() => { helpService.context().then(context => setVersion(context.app_version)).catch(() => {}); }, []);
  useEffect(() => {
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [busy, onClose]);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const report = await helpService.report(description.trim(), window.location.href, file);
      showToast({ type: 'success', title: `Thanks — logged as #${report.github_issue_number || report.id}`, actions: isH2okayAdmin && report.github_issue_url ? [{ label: 'Open issue', onClick: () => window.open(report.github_issue_url, '_blank', 'noopener,noreferrer') }] : undefined });
      onClose();
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not send your report.'); }
    finally { setBusy(false); }
  };
  return <div className="help-modal"><div ref={ref} tabIndex={-1} className="help-card" role="dialog" aria-modal="true" aria-labelledby="report-title">
    <h2 id="report-title">Report a problem</h2>
    <form onSubmit={submit}>
      <label htmlFor="problem-description">What went wrong?</label>
      <textarea id="problem-description" minLength={10} maxLength={5000} required value={description} onChange={event => setDescription(event.target.value)} placeholder="Tell us what you were doing and what happened." />
      <label htmlFor="problem-screenshot">Screenshot (optional, up to 5 MB)</label>
      <input id="problem-screenshot" type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={event => {
        const selected = event.target.files?.[0];
        if (selected && (selected.size > 5 * 1024 * 1024 || !['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(selected.type))) { setError('Choose a PNG, JPEG, GIF or WebP image up to 5 MB.'); event.target.value = ''; setFile(undefined); }
        else { setError(''); setFile(selected); }
      }} />
      <p>On Windows, press Windows + Shift + S. On Mac, press Shift + Command + 4. On a phone, use your usual screenshot buttons.</p>
      <p>We'll include: page, your role, app version.</p>
      <p><small>{window.location.pathname} · {currentUser?.role.replace(/_/g, ' ')} · {version}</small></p>
      {error && <p role="alert">{error}</p>}
      <div className="help-buttons"><button type="button" disabled={busy} onClick={onClose}>Cancel</button><button type="submit" disabled={busy || description.trim().length < 10}>{busy ? 'Sending…' : 'Send report'}</button></div>
    </form>
  </div></div>;
}
